/* ============================================================
   DAAQ Interiores — Rug Finder Quiz
   v2: full catalog load + weighted scoring with progressive relaxation
   ============================================================ */

(function () {
  'use strict';

  const CACHE_KEY = 'daaq_rf_catalog_v2';
  const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

  /* ---------------------------------------------------------
     Semantic mappings — expanded per DAAQ catalog knowledge
     --------------------------------------------------------- */

  // Color palettes → color tags in catalog (Tags field)
  const PALETTE_COLORS = {
    'neutros-claros': ['beige', 'crema', 'blanco', 'ivory'],
    'neutros-oscuros': ['gris', 'negro', 'café', 'cafe', 'plateado'],
    'calidos': ['dorado', 'rojo', 'naranja', 'amarillo', 'rosa', 'bronce'],
    'frios': ['azul', 'verde', 'morado'],
    'multicolor': ['multicolor'],
    'cualquiera': []
  };

  // Shape values in metafield
  const FORMA_MATCH = {
    'rectangular': ['rectangular'],
    'corredor': ['corredor'],
    'redondo': ['redondo'],
    'cuadrado': ['cuadrado'],
    'irregular': ['semicircular', 'piel', 'ovalado', 'otros'],
    'cualquiera': []
  };

  // Estilo mappings — broader to capture cross-style affinity
  // Each style has PRIMARY (heavy match) and SECONDARY (partial match)
  const ESTILO_MATCH = {
    'moderno': {
      primary: ['moderno', 'contemporáneo', 'contemporaneo'],
      secondary: ['transicional', 'minimalista', 'abstracto']
    },
    'minimalista': {
      primary: ['lisos', 'minimalista'],
      secondary: ['contemporáneo', 'contemporaneo', 'moderno']
    },
    'bohemio': {
      primary: ['bohemio'],
      secondary: ['abstracto', 'costero', 'rustico', 'rústico']
    },
    'tradicional': {
      primary: ['tradicional', 'traditional'],
      secondary: ['clasico', 'clásico', 'barroco', 'estilo-granja', 'rustico', 'rústico']
    },
    'geometrico': {
      primary: ['geométrico', 'geometrico'],
      secondary: ['abstracto', 'contemporáneo', 'moderno']
    },
    'costero': {
      primary: ['costero', 'exterior / interior', 'exterior/interior', 'exteriores'],
      secondary: ['bohemio', 'rustico', 'rústico']
    }
  };

  // Materials considered "natural fiber" — reinforces costero
  const NATURAL_FIBERS = ['yute', 'bambu', 'bambú', 'algodon', 'algodón'];

  // Materials that are pet/kid friendly (performance synthetics)
  const PET_KID_MATERIALS = ['poliester', 'poliéster', 'polipropileno', 'polipropileno-pp', 'pp', 'pet'];

  // PET is "premium pet/kid" — better texture
  const PREMIUM_PET_KID = ['pet'];

  // Pile types that lean pet/kid friendly (machine-made, flat-weave, braided)
  const PET_KID_PILE = ['fabricado-a-maquina', 'tejido-plano', 'trenzado'];

  // Outdoor-safe materials
  const OUTDOOR_MATERIALS = ['pet', 'polipropileno', 'polipropileno-pp', 'pp', 'poliester', 'poliéster', 'yute'];

  // Author piece indicators
  const AUTOR_MATERIALS_STRICT = ['lana', 'seda'];
  const AUTOR_PILE = ['anudado-a-mano', 'tejido-a-mano'];

  // Size recommendations (text)
  const SIZE_COPY = {
    a: 'Para tu espacio te recomendamos 160 × 230 cm.',
    b: 'Para tu espacio te recomendamos 200 × 290 cm.',
    c: 'Para tu espacio te recomendamos 240 × 340 cm.',
    d: 'Para tu espacio te recomendamos 300 × 400 cm o más grande.',
    e: 'Explora las opciones y consulta la guía de medidas para elegir mejor.'
  };

  /* =========================================================
     Main class
     ========================================================= */

  class RugFinder {
    constructor(root) {
      this.root = root;
      this.screens = Array.from(root.querySelectorAll('[data-screen]'));
      this.progressBar = root.querySelector('.quiz-rf__progress-bar');
      this.stepLabel = root.querySelector('[data-rf-step-label]');
      this.stepCount = root.querySelector('[data-rf-step-count]');
      this.answers = {};
      this.history = ['intro'];
      this.catalog = [];
      this.catalogReady = null; // Promise
      this.settings = {
        resultsCount: parseInt(root.dataset.resultsCount, 10) || 12,
        minResults: 3,
        sizeGuideUrl: root.dataset.sizeGuideUrl || '',
        contactUrl: root.dataset.contactUrl || '/pages/contacto',
        klaviyoListId: root.dataset.klaviyoList || ''
      };

      this.loadCatalog();
      this.bind();
      this.render();
    }

    /* ---------------------------------------------------------
       Catalog loading — inline first page + background fetch of rest
       --------------------------------------------------------- */
    loadCatalog() {
      let totalPages = 1;
      try {
        const node = this.root.querySelector('[data-rf-catalog]');
        if (node) {
          const parsed = JSON.parse(node.textContent.trim());
          this.catalog = Array.isArray(parsed) ? parsed : (parsed.items || []);
          totalPages = parsed.pages || 1;
          console.log('[RugFinder] Inline page 1 loaded:', this.catalog.length,
                      'items | total_store:', parsed.total_store, '| pages:', totalPages);
        }
      } catch (e) {
        console.warn('[RugFinder] Inline catalog parse error:', e);
        this.catalog = [];
      }

      // Kick off background fetch of remaining pages
      this.catalogReady = this.fetchRemainingPages(totalPages);
    }

    async fetchRemainingPages(totalPages) {
      if (totalPages <= 1) {
        console.log('[RugFinder] Only 1 page, no fetch needed');
        return;
      }
      const pageNums = [];
      for (let i = 2; i <= totalPages; i++) pageNums.push(i);
      console.log('[RugFinder] Fetching pages 2..' + totalPages + ' (' + pageNums.length + ' pages)');

      const results = await Promise.all(
        pageNums.map(n => this.fetchCatalogPage(n).catch(err => {
          console.warn('[RugFinder] Page ' + n + ' failed:', err);
          return null;
        }))
      );

      let added = 0;
      results.forEach(r => {
        if (r && Array.isArray(r.items)) {
          this.catalog = this.catalog.concat(r.items);
          added += r.items.length;
        }
      });
      console.log('[RugFinder] Catalog total after fetch:', this.catalog.length, '(+' + added + ')');
    }

    async fetchCatalogPage(page) {
      // Fetch our own page with section_id → returns just the catalog section HTML
      const url = `/pages/encuentra-tu-tapete?section_id=quiz-rf-catalog&page=${page}`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 12000);
      try {
        const r = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const text = await r.text();
        const match = text.match(/<script[^>]*data-rf-page[^>]*>([\s\S]*?)<\/script>/);
        if (!match) throw new Error('Payload not found');
        return JSON.parse(match[1].trim());
      } finally {
        clearTimeout(timeoutId);
      }
    }

    /* ---------------------------------------------------------
       Interaction
       --------------------------------------------------------- */
    bind() {
      this.root.addEventListener('click', (e) => {
        const opt = e.target.closest('[data-rf-options] .quiz-rf__option');
        if (opt) {
          e.preventDefault();
          this.toggleOption(opt);
          return;
        }
        if (e.target.closest('[data-rf-next]')) {
          e.preventDefault();
          this.next();
        }
        if (e.target.closest('[data-rf-prev]')) {
          e.preventDefault();
          this.prev();
        }
      });

      const form = this.root.querySelector('[data-rf-form]');
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.submitCapture(form);
        });
      }
    }

    toggleOption(btn) {
      const container = btn.closest('[data-rf-options]');
      const field = container.dataset.rfField;
      const type = container.dataset.rfType;
      const max = parseInt(container.dataset.rfMax, 10) || null;
      const value = btn.dataset.rfValue;

      if (type === 'single') {
        container.querySelectorAll('.quiz-rf__option').forEach(o => o.removeAttribute('data-selected'));
        btn.setAttribute('data-selected', 'true');
        this.answers[field] = value;
      } else {
        const current = this.answers[field] || [];
        const idx = current.indexOf(value);
        if (idx > -1) {
          current.splice(idx, 1);
          btn.removeAttribute('data-selected');
        } else {
          if (max && current.length >= max) {
            const oldest = current.shift();
            const oldBtn = container.querySelector(`[data-rf-value="${oldest}"]`);
            if (oldBtn) oldBtn.removeAttribute('data-selected');
          }
          current.push(value);
          btn.setAttribute('data-selected', 'true');
        }
        this.answers[field] = current;
      }

      this.validateCurrentScreen();
    }

    validateCurrentScreen() {
      const screen = this.getCurrentScreen();
      const container = screen.querySelector('[data-rf-options]');
      const nextBtn = screen.querySelector('[data-rf-next], [data-rf-submit]');
      if (!container || !nextBtn) return;

      const required = container.dataset.rfRequired === 'true';
      const field = container.dataset.rfField;
      const val = this.answers[field];
      const filled = Array.isArray(val) ? val.length > 0 : !!val;

      nextBtn.disabled = required && !filled;
    }

    getCurrentScreen() {
      return this.screens.find(s => s.dataset.active === 'true');
    }

    showScreen(name) {
      this.screens.forEach(s => {
        s.dataset.active = s.dataset.screen === name ? 'true' : 'false';
      });
      window.scrollTo({ top: this.root.offsetTop - 40, behavior: 'smooth' });
      this.updateProgress();
      this.validateCurrentScreen();
    }

    updateProgress() {
      const current = this.getCurrentScreen();
      const name = current.dataset.screen;
      const order = this.getScreenOrder();
      const idx = order.indexOf(name);
      const qScreens = order.filter(n => !['intro', 'capture', 'results'].includes(n));
      const total = qScreens.length;
      const answered = Math.max(0, idx - 1);
      const progress = name === 'intro' ? 0 : name === 'results' ? 1 : Math.min(1, answered / total);

      if (this.progressBar) this.progressBar.style.setProperty('--rf-progress', progress);
      if (this.stepCount) {
        if (name === 'intro') this.stepCount.textContent = 'Comencemos';
        else if (name === 'capture') this.stepCount.textContent = 'Casi listo';
        else if (name === 'results') this.stepCount.textContent = 'Tu curaduría';
        else this.stepCount.textContent = `${answered + 1} / ${total}`;
      }
      if (this.stepLabel) {
        const labels = {
          intro: 'Curaduría', audience: 'Para quién', project: 'Proyecto',
          formas: 'Forma', paletas: 'Paleta',
          estilo: 'Estilo', consideraciones: 'Consideraciones', tamano: 'Tamaño',
          capture: 'Correo', results: 'Curaduría'
        };
        this.stepLabel.textContent = labels[name] || '';
      }

      // Dynamically update the eyebrow on the current question screen
      const qIdx = qScreens.indexOf(name);
      if (qIdx > -1) {
        const eyebrow = current.querySelector('[data-rf-eyebrow], .quiz-rf__eyebrow');
        if (eyebrow && !eyebrow.dataset.rfStatic) {
          eyebrow.textContent = `Pregunta ${qIdx + 1} de ${total}`;
        }
      }
    }

    getScreenOrder() {
      const base = ['intro', 'audience'];
      if (this.answers.audience === 'designer') base.push('project');
      base.push('formas', 'paletas', 'estilo', 'consideraciones', 'tamano', 'capture', 'results');
      return base;
    }

    next() {
      const current = this.getCurrentScreen();
      const name = current.dataset.screen;
      const order = this.getScreenOrder();
      const idx = order.indexOf(name);
      if (idx < 0 || idx >= order.length - 1) return;
      const nextName = order[idx + 1];
      this.history.push(nextName);
      this.showScreen(nextName);
    }

    prev() {
      if (this.history.length <= 1) return;
      this.history.pop();
      const prevName = this.history[this.history.length - 1];
      this.showScreen(prevName);
    }

    render() {
      this.updateProgress();
    }

    /* ---------------------------------------------------------
       Capture + integrations
       --------------------------------------------------------- */
    async submitCapture(form) {
      const email = form.querySelector('[name="email"]').value.trim();
      const firstName = form.querySelector('[name="first_name"]').value.trim();
      const acceptsMarketing = form.querySelector('[name="accepts_marketing"]').checked;

      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        form.querySelector('[name="email"]').focus();
        return;
      }

      this.answers.email = email;
      this.answers.first_name = firstName;
      this.answers.accepts_marketing = acceptsMarketing;

      const submitBtn = form.querySelector('[data-rf-submit]');
      submitBtn.disabled = true;
      submitBtn.textContent = 'Preparando tu selección…';

      this.trackKlaviyo(email, firstName);
      this.createShopifyCustomer(email, firstName, acceptsMarketing);

      this.history.push('results');
      this.showScreen('results');
      await this.renderResults();
    }

    trackKlaviyo(email, firstName) {
      try {
        const props = {
          $email: email,
          $first_name: firstName || undefined,
          rug_quiz_audience: this.answers.audience || '',
          rug_quiz_project: this.answers.project || '',
          rug_quiz_formas: (this.answers.formas || []).join(', '),
          rug_quiz_paletas: (this.answers.paletas || []).join(', '),
          rug_quiz_estilo: this.answers.estilo || '',
          rug_quiz_consideraciones: (this.answers.consideraciones || []).join(', '),
          rug_quiz_tamano: this.answers.tamano || ''
        };

        if (window._learnq && typeof window._learnq.push === 'function') {
          window._learnq.push(['identify', props]);
          window._learnq.push(['track', 'Completed Rug Quiz', props]);
        }
        if (window.klaviyo && typeof window.klaviyo.push === 'function') {
          window.klaviyo.push(['identify', props]);
          window.klaviyo.push(['track', 'Completed Rug Quiz', props]);
        }
      } catch (e) { console.warn('[RugFinder] Klaviyo track failed:', e); }
    }

    createShopifyCustomer(email, firstName, acceptsMarketing) {
      // Use fetch POST to /contact — CSP-compatible (no iframe)
      try {
        const formData = new FormData();
        formData.append('form_type', 'customer');
        formData.append('utf8', '✓');
        formData.append('contact[email]', email);
        if (firstName) formData.append('contact[first_name]', firstName);
        formData.append('contact[accepts_marketing]', acceptsMarketing ? 'true' : 'false');
        formData.append('contact[tags]', this.buildCustomerTags().join(','));

        fetch('/contact', {
          method: 'POST',
          body: formData,
          credentials: 'same-origin',
          redirect: 'manual' // Don't follow the redirect to success page
        }).catch(e => console.warn('[RugFinder] Customer POST failed (non-blocking):', e));
      } catch (e) { console.warn('[RugFinder] Customer creation failed:', e); }
    }

    buildCustomerTags() {
      const tags = ['quiz-lead'];
      if (this.answers.audience) tags.push('quiz-audience-' + this.answers.audience);
      if (this.answers.estilo) tags.push('quiz-estilo-' + this.answers.estilo);
      (this.answers.paletas || []).forEach(p => tags.push('quiz-paleta-' + p));
      if (this.answers.tamano) tags.push('quiz-tamano-' + this.answers.tamano);
      return tags;
    }

    /* ---------------------------------------------------------
       Scoring engine — weighted + progressive relaxation
       --------------------------------------------------------- */
    async renderResults() {
      const loading = this.root.querySelector('[data-rf-results-loading]');
      const grid = this.root.querySelector('[data-rf-results-grid]');
      const empty = this.root.querySelector('[data-rf-results-empty]');
      const sizeSuggestion = this.root.querySelector('[data-rf-size-suggestion]');

      // Absolute failsafe: hide loader after 10s no matter what
      const failsafe = setTimeout(() => {
        if (loading && !loading.hidden) {
          loading.hidden = true;
          if (grid.innerHTML === '') {
            empty.hidden = false;
          }
        }
      }, 10000);

      try {
        if (sizeSuggestion && this.answers.tamano) {
          sizeSuggestion.textContent = SIZE_COPY[this.answers.tamano] || '';
        }

        // Wait for catalog to finish loading (up to 6s), then proceed regardless
        await Promise.race([
          this.catalogReady.catch(() => null),
          new Promise(r => setTimeout(r, 6000))
        ]);
        console.log('[RugFinder] Scoring on catalog of:', this.catalog.length, 'products');

        // Small UX delay
        await new Promise(r => setTimeout(r, 800));

        const matches = this.scoreAndFilter();

        loading.hidden = true;

        if (!matches || matches.length === 0) {
          empty.hidden = false;
          grid.hidden = true;
          return;
        }

        grid.hidden = false;
        empty.hidden = true;
        grid.innerHTML = matches.slice(0, this.settings.resultsCount).map(this.renderCard).join('');
      } catch (e) {
        console.error('[RugFinder] renderResults failed:', e);
        if (loading) loading.hidden = true;
        if (empty) empty.hidden = false;
        if (grid) grid.hidden = true;
      } finally {
        clearTimeout(failsafe);
      }
    }

    scoreAndFilter() {
      const a = this.answers;
      const colorSet = (a.paletas || []).flatMap(p => PALETTE_COLORS[p] || []).map(s => s.toLowerCase());
      const shapeSet = (a.formas || []).flatMap(f => FORMA_MATCH[f] || []).map(s => s.toLowerCase());
      const estiloCfg = ESTILO_MATCH[a.estilo] || { primary: [], secondary: [] };
      const estiloPrimary = estiloCfg.primary.map(s => s.toLowerCase());
      const estiloSecondary = estiloCfg.secondary.map(s => s.toLowerCase());
      const consideraciones = new Set(a.consideraciones || []);

      const anyColor = colorSet.length === 0 || (a.paletas || []).includes('cualquiera');
      const anyShape = shapeSet.length === 0 || (a.formas || []).includes('cualquiera');

      const toStr = (v) => {
        if (!v) return '';
        if (Array.isArray(v)) return v.join('|').toLowerCase();
        return String(v).toLowerCase();
      };

      const scored = this.catalog.map(p => {
        const tags = toStr(p.tags).split('|').filter(Boolean);
        const estilo = toStr(p.e);
        const shape = toStr(p.s);
        const material = toStr(p.m);
        const pile = toStr(p.pi);
        const space = toStr(p.sp);

        let score = 0;

        // COLOR (weight: 8)
        if (!anyColor) {
          const matchColor = colorSet.some(c => tags.some(t => t.includes(c)));
          if (matchColor) score += 8;
        } else {
          score += 1; // neutral bonus
        }

        // SHAPE (weight: 6)
        if (!anyShape) {
          const matchShape = shapeSet.some(s => shape.includes(s));
          if (matchShape) score += 6;
        } else {
          score += 1;
        }

        // ESTILO (weight: 10 primary, 5 secondary)
        if (estiloPrimary.length) {
          if (estiloPrimary.some(e => estilo.includes(e))) score += 10;
          else if (estiloSecondary.some(e => estilo.includes(e))) score += 5;
        } else {
          score += 1;
        }

        // COSTERO bonus: natural fibers reinforce
        if (a.estilo === 'costero' && NATURAL_FIBERS.some(m => material.includes(m))) {
          score += 4;
        }

        // CONSIDERACIONES (weight varies)
        if (consideraciones.has('mascotas')) {
          const petKid = PET_KID_MATERIALS.some(m => material.includes(m)) ||
                         PET_KID_PILE.some(pl => pile.includes(pl));
          if (petKid) score += 6;
          // Premium PET bonus
          if (PREMIUM_PET_KID.some(m => material.includes(m))) score += 2;
        }
        if (consideraciones.has('limpieza')) {
          const easy = PET_KID_MATERIALS.some(m => material.includes(m)) ||
                       pile.includes('fabricado-a-maquina') ||
                       pile.includes('tejido-plano');
          if (easy) score += 5;
        }
        if (consideraciones.has('exterior')) {
          const outdoor = space.includes('exterior') ||
                          OUTDOOR_MATERIALS.some(m => material.includes(m));
          if (outdoor) score += 7;
          else score -= 3; // soft penalty (not hard fail)
        }
        if (consideraciones.has('autor')) {
          const lana = material.includes('lana');
          const seda = material.includes('seda');
          const anudado = AUTOR_PILE.some(x => pile.includes(x));
          if ((lana && seda) || anudado) score += 8;
          else if (lana || seda) score += 3;
        }

        // Popularity: rating count is strongest proxy we have
        // Cap at 30 reviews → up to +6 points
        const rc = Number(p.rc) || 0;
        score += Math.min(rc, 30) * 0.2;

        // Best seller tag: strong signal
        if (tags.some(t => t.includes('best'))) score += 3;

        // NEW tag: slight penalty to deprioritize recently uploaded
        if (tags.some(t => t === 'new')) score -= 0.5;

        // Preferred vendors bonus
        const vendor = toStr(p.v);
        if (vendor.includes('surya') || vendor.includes('daaq')) score += 4;

        return { p, score, rc };
      });

      // Sort: primary by score desc, tiebreak by rating count desc, then shuffle
      scored.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        if ((b.rc || 0) !== (a.rc || 0)) return (b.rc || 0) - (a.rc || 0);
        return Math.random() - 0.5;
      });

      // Relaxation: if too few have positive score, return top N regardless
      const withScore = scored.filter(x => x.score > 2); // minimum meaningful score
      const target = Math.max(this.settings.minResults, Math.min(this.settings.resultsCount, 6));

      if (withScore.length >= target) {
        return scored.slice(0, this.settings.resultsCount).map(x => x.p);
      }

      // Not enough — return best effort, always top N from full catalog
      return scored.slice(0, Math.max(target, withScore.length)).map(x => x.p);
    }

    renderCard(p) {
      const price = p.p ? (p.p / 100).toLocaleString('es-MX', {
        style: 'currency', currency: 'MXN', maximumFractionDigits: 0
      }) : '';
      const img = p.img || '';
      const safeTitle = p.t.replace(/"/g, '&quot;').replace(/</g, '&lt;');
      return `
        <a class="quiz-rf__product" href="${p.u}">
          <div class="quiz-rf__product-img">
            ${img ? `<img src="${img}" alt="${safeTitle}" loading="lazy">` : ''}
          </div>
          <h3 class="quiz-rf__product-title">${safeTitle}</h3>
          ${price ? `<div class="quiz-rf__product-price">Desde ${price}</div>` : ''}
        </a>
      `;
    }
  }

  function init() {
    document.querySelectorAll('[data-quiz-rf]').forEach(root => {
      if (!root.__rfInit) {
        root.__rfInit = true;
        new RugFinder(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
